import styled from "@emotion/styled/macro";
import { css } from "@emotion/react";

/* ─── Theme ─── */
const accent = "#005a8b";
const accentDark = "#003f63";
const accentLight = "#e3f0f8";

/* ─── Page Wrapper ─── */
export const EMTCTPageWrapper = styled.div`
    padding: 0 20px; width: 100%;
    @media (max-width: 768px) { padding: 0 12px; }
`;

/* ─── Header ─── */
export const EMTCTHeader = styled.div`
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 20px;
    background: linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%);
    color: #fff; border-radius: 10px; margin-bottom: 20px;
    flex-wrap: wrap; gap: 12px;
    box-shadow: 0 4px 12px rgba(0,90,139,0.25);
    @media (max-width: 768px) { flex-direction: column; align-items: flex-start; padding: 14px 16px; }
`;

export const EMTCTHeaderTitle = styled.div`
    h3 { margin: 0 0 4px 0; font-size: 1.15rem; font-weight: 600; }
    p { margin: 0; font-size: 0.82rem; opacity: 0.85; }
`;

export const EMTCTHeaderStats = styled.div`
    display: flex; gap: 10px; flex-wrap: wrap;
`;

export const StatBadge = styled.div`
    display: flex; align-items: center; gap: 5px;
    padding: 5px 12px; background: rgba(255,255,255,0.15);
    border-radius: 18px; font-size: 0.78rem; font-weight: 500;
    span.count { font-weight: 700; font-size: 0.9rem; }
`;

/* ─── Alert Banner ─── */
export const AlertBanner = styled.div`
    padding: 12px 18px; border-radius: 8px; margin-bottom: 16px;
    display: flex; align-items: flex-start; gap: 10px;
    ${function(p) {
        if (p.type === "critical") return css`
            background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
            border-left: 4px solid #e53935;
        `;
        return css`
            background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
            border-left: 4px solid #ff9800;
        `;
    }}
    .alert-icon { font-size: 1.3rem; flex-shrink: 0; }
    .alert-msg { font-size: 0.84rem; font-weight: 600;
        color: ${function(p) { return p.type === "critical" ? "#b71c1c" : "#e65100"; }};
    }
`;

/* ─── Section Nav ─── */
export const SectionNav = styled.div`
    display: flex; gap: 4px; background: #f0f2f5; border-radius: 10px;
    padding: 4px; margin-bottom: 20px; overflow-x: auto;
    @media (max-width: 768px) { gap: 2px; }
`;

export const SectionBtn = styled.button`
    padding: 10px 14px; border: none; border-radius: 8px;
    font-size: 0.78rem; font-weight: 600; cursor: pointer; transition: all 0.2s;
    display: flex; align-items: center; gap: 5px; white-space: nowrap;
    flex-shrink: 0;
    background: ${function(p) { return p.active ? accent : "transparent"; }};
    color: ${function(p) { return p.active ? "#fff" : "#546e7a"; }};
    &:hover { background: ${function(p) { return p.active ? accent : "#e0e0e0"; }}; }
    .nav-dot {
        width: 8px; height: 8px; border-radius: 50%;
        background: ${function(p) {
            if (p.active) return "rgba(255,255,255,0.6)";
            if (p.done) return "#43a047";
            return "#cfd8dc";
        }};
    }
`;

/* ═══════════ Form Sections ═══════════ */
export const FormSection = styled.div`
    background: #fff; border-radius: 10px; border: 1px solid #e8edf2;
    margin-bottom: 20px; overflow: hidden;
    box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`;

export const SectionHeader = styled.div`
    padding: 14px 20px; border-bottom: 1px solid #f0f2f5;
    display: flex; align-items: center; justify-content: space-between;
    background: ${function(p) { return p.bg || accentLight; }};
    h4 { margin: 0; font-size: 0.92rem; font-weight: 600;
        color: ${function(p) { return p.color || accentDark; }}; }
    .sh-badge { font-size: 0.72rem; font-weight: 600; padding: 3px 10px;
        border-radius: 10px; background: rgba(0,0,0,0.06); }
`;

export const SectionBody = styled.div` padding: 20px; `;

export const FieldGrid = styled.div`
    display: grid; grid-template-columns: repeat(${function(p) { return p.cols || 2; }}, 1fr);
    gap: 16px;
    @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

export const FormField = styled.div`
    label { display: block; font-size: 0.75rem; font-weight: 600; color: #546e7a;
        text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 6px; }
    input, select, textarea {
        width: 100%; padding: 10px 12px; border: 1px solid #cfd8dc;
        border-radius: 6px; font-size: 0.86rem; color: #263238; outline: none;
        transition: border 0.2s;
        &:focus { border-color: ${accent}; box-shadow: 0 0 0 2px rgba(0,90,139,0.12); }
    }
    textarea { resize: vertical; min-height: 60px; }
    .hint { font-size: 0.7rem; color: #90a4ae; margin-top: 3px; }
`;

export const ToggleRow = styled.div`
    display: flex; align-items: center; gap: 10px; padding: 8px 0;
    .tgl-label { font-size: 0.86rem; font-weight: 500; color: #263238; }
`;

export const ToggleSwitch = styled.button`
    width: 42px; height: 24px; border-radius: 12px; border: none;
    cursor: pointer; position: relative; transition: background 0.2s;
    background: ${function(p) { return p.active ? accent : "#cfd8dc"; }};
    flex-shrink: 0;
    &::after {
        content: ""; position: absolute; top: 2px;
        left: ${function(p) { return p.active ? "20px" : "2px"; }};
        width: 20px; height: 20px; border-radius: 50%;
        background: #fff; transition: left 0.2s;
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    }
`;

/* ─── Testing Algorithm Visual ─── */
export const AlgoContainer = styled.div`
    display: flex; align-items: center; justify-content: center;
    gap: 0; padding: 20px 0; overflow-x: auto;
    @media (max-width: 768px) { flex-direction: column; gap: 0; }
`;

export const AlgoStep = styled.div`
    display: flex; flex-direction: column; align-items: center;
    text-align: center; min-width: 140px; position: relative;
    padding: 14px;
    ${function(p) {
        if (p.active) return css`
            .algo-box {
                border-color: ${accent}; background: ${accentLight};
                box-shadow: 0 0 0 3px rgba(0,90,139,0.15);
            }
        `;
        if (p.done) return css`
            .algo-box { border-color: #43a047; background: #e8f5e9; }
        `;
        return css`
            .algo-box { border-color: #e0e0e0; background: #fafafa; opacity: 0.6; }
        `;
    }}
`;

export const AlgoBox = styled.div`
    width: 120px; padding: 12px; border: 2px solid; border-radius: 10px;
    transition: all 0.3s;
    .algo-name { font-size: 0.78rem; font-weight: 700; color: #263238; }
    .algo-desc { font-size: 0.68rem; color: #78909c; margin-top: 4px; }
    .algo-result { font-size: 0.72rem; font-weight: 700; margin-top: 6px;
        padding: 2px 8px; border-radius: 4px; display: inline-block; }
`;

export const AlgoArrow = styled.div`
    font-size: 1.2rem; color: #90a4ae; flex-shrink: 0;
    @media (max-width: 768px) { transform: rotate(90deg); }
`;

/* ─── Conditional Section ─── */
export const ConditionalBanner = styled.div`
    padding: 12px 16px; border-radius: 8px; margin-bottom: 16px;
    display: flex; align-items: center; gap: 10px;
    font-size: 0.84rem;
    ${function(p) {
        if (p.variant === "positive") return css`
            background: #ffebee; border: 1px solid #ef9a9a; color: #b71c1c;
        `;
        if (p.variant === "negative") return css`
            background: #e8f5e9; border: 1px solid #a5d6a7; color: #2e7d32;
        `;
        return css`
            background: #f5f5f5; border: 1px solid #e0e0e0; color: #546e7a;
        `;
    }}
    .cb-icon { font-size: 1.2rem; }
    .cb-text { font-weight: 600; }
`;

/* ─── Save Button ─── */
export const SaveBtn = styled.button`
    padding: 12px 32px; border-radius: 8px; border: none;
    background: ${accent}; color: #fff; font-size: 0.9rem; font-weight: 600;
    cursor: pointer; transition: all 0.2s;
    &:hover { background: ${accentDark}; }
    &:disabled { background: #7aaec8; cursor: not-allowed; }
`;

/* ─── Empty / Loading ─── */
export const EmptyState = styled.div`
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; padding: 60px 20px; text-align: center; color: #90a4ae;
    .empty-icon { font-size: 3rem; margin-bottom: 16px; }
    .empty-title { font-size: 1.05rem; font-weight: 600; color: #546e7a; margin-bottom: 8px; }
    .empty-desc { font-size: 0.85rem; max-width: 380px; line-height: 1.5; }
`;

export const LoadingWrapper = styled.div`
    display: flex; align-items: center; justify-content: center;
    padding: 60px 20px; color: #90a4ae; font-size: 0.9rem; gap: 10px;
`;

export const GoToBookingBtn = styled.button`
    margin-top: 20px; padding: 10px 24px; background: ${accent}; color: #fff;
    border: none; border-radius: 8px; font-size: 0.9rem; font-weight: 600;
    cursor: pointer; transition: background 0.2s;
    &:hover { background: ${accentDark}; }
`;

/* ─── View Toggle ─── */
export const ViewToggleBar = styled.div`
    display: flex; gap: 8px; margin-bottom: 16px;
`;

export const ViewToggleBtn = styled.button`
    padding: 8px 18px; border-radius: 8px; border: none;
    font-size: 0.84rem; font-weight: 600; cursor: pointer; transition: all 0.2s;
    background: ${function(p) { return p.active ? accent : "#f0f2f5"; }};
    color: ${function(p) { return p.active ? "#fff" : "#546e7a"; }};
    &:hover { background: ${function(p) { return p.active ? accentDark : "#e0e4e8"; }}; }
`;

/* ─── History Timeline ─── */
export const HistoryContainer = styled.div`
    display: flex; flex-direction: column; gap: 14px;
`;

export const HistoryCard = styled.div`
    background: #fff; border-radius: 10px;
    border: 1px solid #e8edf2;
    box-shadow: 0 1px 4px rgba(0,0,0,0.05);
    overflow: hidden;
`;

export const HistoryCardHeader = styled.div`
    display: flex; align-items: center; justify-content: space-between;
    flex-wrap: wrap; gap: 8px;
    padding: 12px 18px;
    background: ${accentLight};
    border-bottom: 1px solid #d0e4f0;
`;

export const HistoryCardDate = styled.div`
    font-size: 0.84rem; font-weight: 700; color: ${accentDark};
`;

export const HistoryCardMeta = styled.div`
    font-size: 0.75rem; color: #78909c;
`;

export const HistoryCardBody = styled.div`
    padding: 14px 18px;
    display: flex; flex-direction: column; gap: 10px;
`;

export const HistoryBadgeRow = styled.div`
    display: flex; flex-wrap: wrap; gap: 8px; align-items: center;
`;

export const HistoryBadge = styled.span`
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 10px; border-radius: 12px; font-size: 0.75rem; font-weight: 600;
    background: ${function(p) { return p.bg || "#f0f2f5"; }};
    color: ${function(p) { return p.color || "#546e7a"; }};
    border: 1px solid ${function(p) { return p.border || "transparent"; }};
`;

export const HistoryDetailRow = styled.div`
    display: flex; flex-wrap: wrap; gap: 16px;
`;

export const HistoryDetailItem = styled.div`
    font-size: 0.8rem; color: #546e7a;
    strong { color: #263238; font-weight: 600; }
`;

export const HistorySkeletonCard = styled.div`
    background: #fff; border-radius: 10px; border: 1px solid #e8edf2;
    padding: 16px 18px; display: flex; flex-direction: column; gap: 10px;
`;

export const HistorySkeletonLine = styled.div`
    height: ${function(p) { return p.h || "14px"; }};
    width: ${function(p) { return p.w || "100%"; }};
    background: linear-gradient(90deg, #f0f2f5 25%, #e8edf2 50%, #f0f2f5 75%);
    background-size: 200% 100%;
    border-radius: 4px;
    animation: shimmer 1.4s infinite;
    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
`;
